<template>
    <div class="w-full py-6">
        <div
            class="mb-4 flex gap-3"
            :class="isDesktop ? 'items-center justify-between' : 'flex-col'"
        >
            <h3
                class="text-sm font-semibold uppercase tracking-widest text-stack-text"
            >
                {{ sectionTitle }}
            </h3>

            <div
                class="flex gap-2"
                :class="isDesktop ? 'items-center' : 'w-full'"
            >
                <button
                    v-if="canUseTemplates"
                    type="button"
                    :class="{ 'flex-1': !isDesktop }"
                    class="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-gray px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:opacity-90"
                    @click="openTemplatesModal"
                >
                    Usar plantilla
                </button>

                <custom-tooltip
                    v-if="canAddSegment && isLimitReached"
                    border
                    :class="{ 'flex-1': !isDesktop }"
                >
                    <button
                        type="button"
                        disabled
                        :title="limitReachedMessage"
                        class="inline-flex w-full cursor-not-allowed items-center justify-center gap-1.5 rounded-lg bg-theme-hover px-4 py-2 text-sm font-medium text-theme-muted shadow-sm transition"
                    >
                        <plus-icon class="h-4 w-4" />
                        {{ addButtonLabel }}
                    </button>
                    <template #tooltip>
                        {{ limitReachedMessage }}
                    </template>
                </custom-tooltip>
                <button
                    v-else-if="canAddSegment"
                    type="button"
                    :class="{ 'flex-1': !isDesktop }"
                    class="inline-flex items-center justify-center gap-1.5 rounded-lg bg-theme-success px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:opacity-90"
                    @click="openAddModal"
                >
                    <plus-icon class="h-4 w-4" />
                    {{ addButtonLabel }}
                </button>
            </div>
        </div>

        <div
            v-if="!visibleSegments.length"
            class="rounded-lg border border-dashed border-theme-border py-12 text-center text-sm text-brand-gray"
        >
            {{ emptyStateMessage }}
        </div>

        <div v-else class="flex flex-col">
            <template v-for="{ segment, index } in visibleSegments">
                <segment-block-card
                    :key="segmentId(segment)"
                    :ref="segmentCardRef(segment)"
                    :segment="segment"
                    :stack="stack"
                    :colors="colors"
                    :dark="dark"
                    :is-first="index === 0"
                    :is-last="index === segments.length - 1"
                    :is-recently-moved="recentlyMovedSegment === segment"
                    :is-desktop="isDesktop"
                    :is-links-only-mode="isLinksOnlyMode"
                    @move-up="moveSegment(index, -1)"
                    @move-down="moveSegment(index, 1)"
                    @edit="openEditModal(segment, index)"
                    @remove="removeSegment(index)"
                />
                <!-- "+" debajo de cada bloque (también del último, solo existe
                     si hay al menos un bloque): inserta justo ahí.
                     bg-brand-gray + text-white porque va sobre el fondo del
                     catálogo del tenant (ver portal/CLAUDE.md). Visible siempre
                     en pantallas chicas (sin hover); desde md aparece al
                     pasar el mouse por el hueco. -->
                <div
                    :key="`insert-${segmentId(segment)}`"
                    class="relative flex h-6 items-center justify-center"
                    :class="{
                        'opacity-100 md:opacity-0 md:hover:opacity-100 md:focus-within:opacity-100':
                            canInsertSegment
                    }"
                >
                    <template v-if="canInsertSegment">
                        <span
                            class="absolute inset-x-0 top-1/2 h-px bg-brand-gray"
                        ></span>
                        <button
                            type="button"
                            :aria-label="insertButtonLabel"
                            :title="insertButtonLabel"
                            class="relative z-10 flex h-6 w-6 items-center justify-center rounded-full bg-brand-gray text-white shadow-sm transition hover:opacity-90"
                            @click="openInsertModal(index + 1)"
                        >
                            <plus-icon class="h-3.5 w-3.5" />
                        </button>
                    </template>
                </div>
            </template>
        </div>

        <add-segment-modal
            ref="addSegmentModal"
            v-model="modalOpen"
            :stack="stack"
            :colors="colors"
            :dark="dark"
            :allowed-types="allowedTypes"
            :is-links-only-mode="isLinksOnlyMode"
            @confirm="onConfirm"
        />

        <segment-templates-modal
            ref="segmentTemplatesModal"
            v-model="templatesModalOpen"
            :segments="segments"
            :stack="stack"
            :colors="colors"
            :dark="dark"
            @apply-full="onApplyFullTemplate"
            @apply-single="onApplySingleTemplate"
        />
    </div>
</template>

<script>
import { generateBlockId } from "@/common/fields/catalog-blocks";

import AddSegmentModal from "@/components/editor/AddSegmentModal.vue";
import SegmentBlockCard from "@/components/editor/SegmentBlockCard.vue";
import SegmentTemplatesModal from "@/components/editor/SegmentTemplatesModal.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import CustomTooltip from "@/components/utils/CustomTooltip.vue";

// Tope del editor = cuota del plan más alto (Premium). La cuota real de cada
// plan la valida el backend al guardar, que abre el modal "Pásate a…".
const MAX_SEGMENTS = 100;
// Fallback de id para segments viejos cargados sin id (ver bug reportado de
// reorden con flechas: una key inestable por índice hace que Vue reutilice
// nodos del DOM y dé sensación de "salto"). Se cachea por referencia de
// objeto para que la key no cambie entre renders del mismo segment.
const fallbackSegmentIds = new WeakMap();

export default {
    name: "SegmentsEditor",
    components: {
        AddSegmentModal,
        SegmentBlockCard,
        SegmentTemplatesModal,
        PlusIcon,
        CustomTooltip
    },
    model: {
        prop: "segments",
        event: "input"
    },
    props: {
        segments: {
            type: Array,
            default: () => []
        },
        stack: {
            type: Object,
            required: true
        },
        colors: {
            type: Object,
            required: true
        },
        dark: {
            type: Boolean,
            default: false
        },
        isDesktop: {
            type: Boolean,
            default: true
        },
        allowedTypes: {
            type: Array,
            default: null
        },
        isLinksOnlyMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            modalOpen: false,
            templatesModalOpen: false,
            editingIndex: -1,
            insertIndex: null,
            recentlyMovedSegment: null
        };
    },
    computed: {
        sectionTitle() {
            return this.isLinksOnlyMode ? "Enlaces" : "Bloques";
        },
        insertButtonLabel() {
            return "Agregar bloque aquí";
        },
        canInsertSegment() {
            return (
                this.canAddSegment &&
                !this.isLimitReached &&
                !this.isLinksOnlyMode
            );
        },
        addButtonLabel() {
            return this.isLinksOnlyMode ? "Agregar enlace" : "Agregar bloque";
        },
        limitReachedMessage() {
            return this.isLinksOnlyMode
                ? `Alcanzaste el máximo de ${MAX_SEGMENTS} enlaces`
                : `Alcanzaste el máximo de ${MAX_SEGMENTS} bloques`;
        },
        emptyStateMessage() {
            return this.isLinksOnlyMode
                ? "Todavía no agregaste ningún enlace."
                : "Todavía no agregaste ningún bloque.";
        },
        visibleSegments() {
            return this.segments
                .map((segment, index) => ({ segment, index }))
                .filter(
                    ({ segment }) =>
                        !this.allowedTypes ||
                        this.allowedTypes.includes(segment.type)
                );
        },
        isLimitReached() {
            return this.segments.length >= MAX_SEGMENTS;
        },
        canUseTemplates() {
            return !this.isLinksOnlyMode;
        },
        canAddSegment() {
            if (this.isLinksOnlyMode && this.visibleSegments.length > 0) {
                return false;
            }

            const isAllowed = (type) =>
                !this.allowedTypes || this.allowedTypes.includes(type);
            return (
                (isAllowed("links") &&
                    this.$ability.hasPermission("customLink:List")) ||
                (isAllowed("productsCarousel") &&
                    this.$ability.hasPermission("product:List")) ||
                isAllowed("text") ||
                isAllowed("media")
            );
        }
    },
    beforeDestroy() {
        clearTimeout(this.recentlyMovedTimeout);
    },
    methods: {
        openAddModal() {
            this.editingIndex = -1;
            this.insertIndex = null;
            this.$refs.addSegmentModal.openForAdd();
        },
        openInsertModal(index) {
            this.editingIndex = -1;
            this.insertIndex = index;
            this.$refs.addSegmentModal.openForAdd();
        },
        openTemplatesModal() {
            this.templatesModalOpen = true;
        },
        onApplyFullTemplate({ mode, segments }) {
            if (mode === "replace") {
                this.$emit("input", segments);
                return;
            }
            const combined =
                mode === "prepend"
                    ? [...segments, ...this.segments]
                    : [...this.segments, ...segments];
            this.$emit("input", combined.slice(0, MAX_SEGMENTS));
        },
        onApplySingleTemplate(segment) {
            if (this.segments.length >= MAX_SEGMENTS) return;
            this.$emit("input", [...this.segments, segment]);
        },
        openEditModal(segment, index) {
            this.editingIndex = index;
            this.$refs.addSegmentModal.openForEdit(segment);
        },
        onConfirm(segment) {
            if (this.editingIndex > -1) {
                const updated = [...this.segments];
                updated[this.editingIndex] = segment;
                this.$emit("input", updated);
                this.editingIndex = -1;
            } else if (this.insertIndex !== null) {
                const updated = [...this.segments];
                updated.splice(this.insertIndex, 0, segment);
                this.$emit("input", updated);
                this.insertIndex = null;
            } else {
                this.$emit("input", [...this.segments, segment]);
            }
        },
        removeSegment(index) {
            const updated = [...this.segments];
            updated.splice(index, 1);
            this.$emit("input", updated);
        },
        moveSegment(index, direction) {
            const targetIndex = index + direction;
            if (targetIndex < 0 || targetIndex >= this.segments.length) return;

            const updated = [...this.segments];
            const [moved] = updated.splice(index, 1);
            updated.splice(targetIndex, 0, moved);
            this.$emit("input", updated);

            this.recentlyMovedSegment = moved;
            clearTimeout(this.recentlyMovedTimeout);
            this.recentlyMovedTimeout = setTimeout(() => {
                this.recentlyMovedSegment = null;
            }, 600);

            this.$nextTick(() => this.scrollSegmentIntoView(moved));
        },
        segmentId(segment) {
            if (segment.id) return segment.id;
            if (!fallbackSegmentIds.has(segment)) {
                fallbackSegmentIds.set(segment, generateBlockId());
            }
            return fallbackSegmentIds.get(segment);
        },
        segmentCardRef(segment) {
            return `segmentCard-${this.segmentId(segment)}`;
        },
        scrollSegmentIntoView(segment) {
            const refName = this.segmentCardRef(segment);
            const card = this.$refs[refName]?.[0] || this.$refs[refName];
            const el = card?.$el;
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }
};
</script>
